import { useEffect } from 'react'

import { isBoolean } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import { MenuItem, MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { DropdownMenu } from '@/Atomic/Organisms/DropdownMenu'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useMediaQuery } from '@/hooks/useMediaQuery'

interface AutomationEventActionMenuProps {
  menuItems: MenuItemProps[]
  onOpenChange: (isOpen: boolean) => void
  trigger?: boolean
}

export const AutomationEventActionMenu: React.FC<
  AutomationEventActionMenuProps
> = ({ menuItems, onOpenChange, trigger }) => {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 766px)')
  const { buttonRef, menuIsOpen, setMenuIsOpen, toggleMenu } = useIconMenu()

  useEffect(() => {
    if (isMobile) onOpenChange(menuIsOpen)
  }, [isMobile, menuIsOpen, onOpenChange])

  useEffect(() => {
    if (isMobile && isBoolean(trigger)) toggleMenu()
  }, [isMobile, toggleMenu, trigger])

  if (!isMobile) {
    return (
      <DropdownMenu
        boundarySelector=".scrollable-panel__body"
        buttonClassName="borderless"
        menuItems={menuItems}
        onOpenChange={onOpenChange}
        trigger={trigger}
      />
    )
  }

  const closeMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setMenuIsOpen(false)
  }

  return (
    <div ref={buttonRef} className="automation-event-action-menu">
      <Button
        icon="more-horizontal"
        variant="icon"
        size="sm"
        tooltip={t('tooltip.more')}
        buttonClassName="o-menu-button borderless"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          toggleMenu()
        }}
      />
      <MenuSheet
        isOpen={menuIsOpen}
        onBackdropClick={closeMenu}
        onMobileCloseClick={closeMenu}
      >
        <div className="automation-event-action-menu__list">
          {menuItems.map((item, index) => (
            <MenuItem
              key={`${item.label}-${index}`}
              {...item}
              onClick={(event) => {
                item.onClick?.(event)
                setMenuIsOpen(false)
              }}
            />
          ))}
        </div>
      </MenuSheet>
    </div>
  )
}
